import { DealQuestion, DealValue, EnumDataTypes } from ".";

/**
 * Represents a row of deal values, mapping model value IDs to their corresponding DealValue objects.
 */
export class DealValueRow {

  rowNo: number;

  /**
   * A map of model value IDs to a map of time segments and DealValue objects.
   * Structure: modelValueId -> timeSeg -> DealValue
   */
  values: Map<string, Map<number, DealValue>>;

  constructor(rowNo: number) {
    this.rowNo = rowNo;
    this.values = new Map<string, Map<number, DealValue>>();
  }

  public addValue(value: DealValue, timeSeg: number): void {

    const modelValueId = value.modelValueId!;

    if (!this.values.has(modelValueId)) {
      this.values.set(modelValueId, new Map<number, DealValue>());
    }

    this.values.get(modelValueId)!.set(timeSeg, value);
  }

  /**
   * Gets the value for a specific modelValueId and time segment.
   * If timeSeg is omitted, returns value for the lowest available timeSeg.
   */
  public getValue(modelValueId: string, timeSeg: number): any | null {

    const timeSegMap = this.values.get(modelValueId);
    if (!timeSegMap || timeSegMap.size === 0) {
      return null;
    }

    let value: DealValue | undefined;
    value = timeSegMap.get(timeSeg);

    if (!value)
      return null;

    if (value.options && value.options.length > 0) {
      return value.options[0].val;
    }

    switch (value.dataTypeCode) {
      case EnumDataTypes.Number:
        return value.nVal;
      case EnumDataTypes.DateTime:
        return value.dtVal;
      default:
        return value.sVal;
    }
  }

  /**
   * Outputs values in order of modelValueIds with each time segment represented.
   * If multiple time segments exist for a modelValueId, multiple columns are returned.
   */
  public toRowArray(orderedModelValueIds: string[]): any[] {

    const row: any[] = [];

    for (let i = 0; i < orderedModelValueIds.length; i++) {

      const compKey = orderedModelValueIds[i];
      const [modelValueId, timeSeg] = this.parseCompositeKey(compKey);

      const value = this.getValue(modelValueId, timeSeg);

      row.push(value);
    }

    return row;
  }

  /**
   * Converts this row's values to a DealValue[] array in expected format.
   */
  public toDealValueArray(rowNo: number, orderedDealQu: DealQuestion[]): DealValue[] {

    const result: DealValue[] = [];

    for (let i = 0; i < orderedDealQu.length; i++) {

      const config = orderedDealQu[i];
      const timeSegMap = this.values.get(config.id!);

      if (timeSegMap) {

        const sortedTimeSegs = Array.from(timeSegMap.keys()).sort((a, b) => a - b);

        for (const timeSeg of sortedTimeSegs) {

          const dv = timeSegMap.get(timeSeg)!;

          // deliberately wipe out the deal value id as we are now clearing down data on the server.
          dv.id = null;
          // reassign the row number so we can return data sequentially
          dv.rowNo = rowNo;

          result.push(dv);
        }
      }

    }

    return result;
  }

  protected parseCompositeKey(input: string): [string, number] {

    if (!input || input.trim() === '') {
      return ['', 1];
    }

    const parts = input.split(':');
    const key = parts[0] || '';
    const segment = parseInt(parts[1], 10);

    return [key, isNaN(segment) ? 1 : segment];
  }

}

